import { Autocomplete, TextField } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRemoteFetchCompanyLocations } from "../../../../hooks/Remote/Location/useRemoteFetchCompanyLocations";

type VisitorLocationOption = {
  id: string;
  name: string;
};

type P = {
  value: string;
  onChange: (locationName: string) => void;
  disabled?: boolean;
};

export function ScheduleSelectionVisitorLocationField({
  value,
  onChange,
  disabled = false
}: Readonly<P>) {
  const { t } = useTranslation();

  const { data: companyLocations, isLoading } = useRemoteFetchCompanyLocations({
    isFinanceRequest: false,
    noMobileWorking: true,
    isBookingSettings: true
  });

  const locationOptions = useMemo(() => {
    const remoteLocations = (companyLocations ?? [])
      .map(location => {
        const locationName = location.name?.trim();
        return locationName ? { id: String(location.id), name: locationName } : null;
      })
      .filter((location): location is VisitorLocationOption => Boolean(location));

    const uniqueByName = Array.from(
      new Map(remoteLocations.map(item => [item.name, item])).values()
    );
    const trimmed = value.trim();
    if (trimmed && !uniqueByName.some(option => option.name === trimmed)) {
      uniqueByName.push({ id: trimmed, name: trimmed });
    }

    return uniqueByName;
  }, [companyLocations, value]);

  const selected = locationOptions.find(option => option.name === value.trim()) ?? null;

  return (
    <Autocomplete
      options={locationOptions}
      fullWidth
      disabled={disabled || isLoading}
      value={selected}
      getOptionLabel={option => option.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_, next) => {
        onChange(next?.name ?? "");
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={t("Location")}
          data-testid="schedule-selection-visitor-location-field"
        />
      )}
    />
  );
}
